/**
 * ado AI's adds to cart → the nav badge and the "Added to cart · View cart" toast.
 *
 * The Clarity Brand Agent chat ("ado AI", app embed #ads-agent-host) adds to the real Shopify cart with its own
 * fetch('/cart/add.js') from inside its shadow root, so the theme never hears about it: the bag badge stays stale and
 * the chat's own "Go to cart" chip only answers in text (the agent can navigate to checkout, not to the cart).
 * This answers those adds the way the theme's quick add does: document 'ado:cart:updated' {detail:{item_count}}
 * (ado-nav bumps the badge) and the shared toast from @theme/ado-quick-add, placed beside the open chat.
 *
 * An add counts as the chat's when the shopper's last pointer or key input was inside the chat host. Events from the
 * shadow tree reach the document retargeted to the host, so composedPath() shows it. Theme buttons outside the host
 * announce their own adds and are left alone. Adds are seen through Resource Timing, so no fetch is wrapped.
 * On /cart the list itself doesn't refresh; the toast's "View cart" reloads it.
 */

const HOST_ID = 'ads-agent-host';
const ADD_PATH = /\/cart\/add(?:\.js)?$/;
/** Same tones as snippets/ado-product-meta.liquid. */
const TONES = /** @type {Record<string, string>} */ ({ facewash: 'sky', moisturiser: 'butter', sunscreen: 'peach', kit: 'lilac' });

/**
 * @typedef {{ key: string, quantity: number, handle?: string, product_title?: string, image?: string | null }} CartItem
 * @typedef {{ item_count: number, items: CartItem[] }} Cart
 */

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
let lastInputInChat = false;
/** @type {Map<string, number> | null} quantities by line key before the chat's next add */
let before = null;
/** @type {number | undefined} */
let settleTimer;

function chatHost() {
  return document.getElementById(HOST_ID);
}

/** @returns {Promise<Cart | null>} */
function fetchCart() {
  return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);
}

/** @param {Cart} cart */
function quantities(cart) {
  return new Map(cart.items.map((item) => [item.key, item.quantity]));
}

/** @param {Event} event */
function onInput(event) {
  const host = chatHost();
  lastInputInChat = Boolean(host) && event.composedPath().includes(/** @type {EventTarget} */ (host));
  if (!lastInputInChat || before) return;
  // First touch of the chat: remember the cart so the next add can be named, and create the toast's live region
  // now (empty) so screen readers announce it when it fills.
  before = new Map();
  fetchCart().then((cart) => {
    if (cart) before = quantities(cart);
  });
  import('@theme/ado-quick-add').then((module) => module.ensureToast()).catch(() => {});
}

/** The open chat panel: the largest visible fixed box in the widget's shadow root, ignoring the launcher pill. */
function chatPanelRect() {
  const shadow = chatHost()?.shadowRoot;
  if (!shadow) return null;
  /** @type {DOMRect | null} */
  let best = null;
  for (const el of shadow.querySelectorAll('div')) {
    const style = getComputedStyle(el);
    if (style.position !== 'fixed' || style.visibility === 'hidden' || Number(style.opacity) === 0) continue;
    const rect = el.getBoundingClientRect();
    if (rect.height < 200 || rect.width < 200) continue;
    if (!best || rect.width * rect.height > best.width * best.height) best = rect;
  }
  return best;
}

/** @param {string | null | undefined} src */
function thumb(src) {
  if (!src) return undefined;
  try {
    const url = new URL(src, window.location.href);
    url.searchParams.set('width', '160');
    return url.href;
  } catch {
    return src;
  }
}

async function onChatAdd() {
  const cart = await fetchCart();
  if (!cart || !Array.isArray(cart.items)) return;
  const previous = before;
  before = quantities(cart);
  document.dispatchEvent(new CustomEvent('ado:cart:updated', { bubbles: true, detail: { item_count: cart.item_count } }));

  // The line that grew. With no snapshot every line counts, and Shopify lists the newest line first.
  const item = cart.items.find((line) => line.quantity > (previous?.get(line.key) || 0));
  if (!item) return;

  const { showToast, stepKey } = await import('@theme/ado-quick-add');
  showToast({
    title: item.product_title || 'Item',
    image: thumb(item.image),
    tone: TONES[stepKey(item.handle || '')] || 'cream',
    cartUrl: `${root}cart`,
    anchor: chatPanelRect(),
  });
}

/** @param {PerformanceObserverEntryList} list */
function onResources(list) {
  if (!lastInputInChat) return;
  for (const entry of /** @type {PerformanceResourceTiming[]} */ (list.getEntries())) {
    if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') continue;
    let path = '';
    try {
      path = new URL(entry.name).pathname;
    } catch {
      continue;
    }
    if (!ADD_PATH.test(path)) continue;
    // responseStatus is 0 where the browser doesn't expose it; a failed add then simply finds no grown line
    if (entry.responseStatus >= 400) continue;
    // One toast for a burst of adds (a kit, or a retry)
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(onChatAdd, 250);
  }
}

if ('PerformanceObserver' in window && PerformanceObserver.supportedEntryTypes?.includes('resource')) {
  document.addEventListener('pointerdown', onInput, true);
  document.addEventListener('keydown', onInput, true);
  new PerformanceObserver(onResources).observe({ type: 'resource' });
}
